import {
  CELL_FILTER,
  CELL_RENDERER,
  FilterOptionType,
  IBaseEntityType,
  IModalDialogOptions,
  ITransferData,
  ITransferDestination,
  REPLENISH_APPLY_ITEMS_SCOPE,
  SORT_ORDERS,
  TRANSFER_TYPE,
  WIDGET_COMPONENT,
} from '../../types';
import { chain, concat, every, isEmpty, isNil, join, map, merge } from 'lodash';
import {
  BTN_CANCEL,
  BTN_CONTINUE,
  BTN_DONE,
  BTN_OK,
} from '@text-content/button-templates';
import {
  EVENT_SELECTOR_SELECT,
  PERCENTAGE_SIGN,
  POSITION_RIGHT,
  WIDGET_FAMILY,
} from '@config/global-constants';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { stringCaseInsensitiveComparator } from '@shared/common';
import {
  DUPLICATE_TRANSFER_ERROR_OPTIONS,
  REPLENISH_ITEM_OPTIONS,
  TRANSFER_REPLENISH_DEFAULT_PERCENT,
} from '@shared/services/transfer/transfer-constants';
import { IReplenishApplyItemScopeOption } from '../transfer-replenish/transfer-replenish.types';
import { getReplenishItemScopeOptions } from '../replenish-worker/replenish.utils';
import { getTransferEventQueryFilter } from './transfer.query.util';
import { Observable } from '@shared/rx';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

const percentCellRendererDefaultParams = {
  defaultValue: TRANSFER_REPLENISH_DEFAULT_PERCENT,
  symbolPosition: POSITION_RIGHT,
  precision: 0,
  label: PERCENTAGE_SIGN,
  max: 999,
  min: 1,
  usePreferenceDecimalPrecision: {
    overrideMin: false,
    overrideMax: false,
  },
  disableKeyUpFinishEditing: true,
  allowEmptyValue: true,
  forceConversionOfZeroToEmpty: true,
};

const getReplenishItemOptions = (fieldItemOptions: {
  showItemOptions: boolean;
  itemOptionsClass: string;
}): {
  omit: boolean;
  title: string;
  widget: WIDGET_COMPONENT;
  widgetFamily: WIDGET_FAMILY;
  required: boolean;
  defaultValue: REPLENISH_APPLY_ITEMS_SCOPE;
  class: string;
  selectOptions: IReplenishApplyItemScopeOption[];
} => ({
  class: fieldItemOptions.itemOptionsClass,
  omit: !fieldItemOptions.showItemOptions,
  title: REPLENISH_ITEM_OPTIONS,
  widget: WIDGET_COMPONENT.select,
  widgetFamily: WIDGET_FAMILY.XUX,
  required: true,
  defaultValue: REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
  selectOptions: getReplenishItemScopeOptions(),
});

/**@deprecated
 * it seems not used anywhere.
 * so we not translated for now
 */
const bulkReplenishToParModal = (
  transferDestination: ITransferData[] | ITransferDestination[],
  showItemOptions: boolean = true,
  title: string = null,
  i18nService: XVII18nService,
): IModalDialogOptions => {
  return merge(
    {
      schema: {
        properties: {
          itemOption: { type: 'string', required: showItemOptions },
          items: { type: 'array', required: true },
        },
      },
    },
    gridSelectModalOptions(transferDestination, i18nService),
    defaultWidgetData,
    {
      title: isEmpty(title) ? 'Bulk Replenish to PAR' : title,
      modalClass: `modal-lg`,
      value: {
        itemOption: REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
        items: transferDestination,
      },
      formRules: {
        fields: {
          itemOption: getReplenishItemOptions({
            showItemOptions,
            itemOptionsClass: 'select-full-width',
          }),
          items: {
            title: '',
            widget: WIDGET_COMPONENT.edit_grid,
            widgetData: {
              showSearch: true,
              multiselect: false,
              orderControls: false,
              disableColumnSelector: true,
              showPinnedRow: true,
              valueField: 'id',
              ignoreItemValueOptimization: true,
              pinnedTopRowData: [
                {
                  changes: null,
                  current: null,
                  schedule_info: [],
                  site: { id: 'pinned' },
                },
              ],
              columns: [
                {
                  title: I18n.Sites.SiteId,
                  path: 'store_number',
                  defaultView: true,
                  sortable: true,
                  comparator: stringCaseInsensitiveComparator,
                  pinnedRowCellRenderer: CELL_RENDERER.count_renderer,
                  pinnedRowCellRendererParams: {
                    label: 'All Sites',
                  },
                  filterParams: {
                    quickFilter: true,
                  },
                },
                {
                  title: I18n.Sites.SiteName,
                  path: 'name',
                  defaultView: true,
                  comparator: stringCaseInsensitiveComparator,
                  sortable: true,
                  filterParams: {
                    quickFilter: true,
                  },
                },
                {
                  title: I18n.Forms.ReplenishForm.ReplenishToPercentOfPAR,
                  path: 'replenish_percent',
                  defaultView: true,
                  cellRenderer: CELL_RENDERER.price_input_renderer,
                  cellRendererParams: percentCellRendererDefaultParams,
                  pinnedRowCellRenderer: CELL_RENDERER.price_input_renderer,
                  pinnedRowCellRendererParams: merge(
                    {},
                    percentCellRendererDefaultParams,
                    { placeholder: I18n.Forms.ReplenishForm.SetAll },
                  ),
                },
              ],
            },
          },
        },
      },
      buttons: [
        {
          ...BTN_CONTINUE,
          disabled: (formValue): boolean =>
            every(formValue.items, (item) => isNil(item.replenish_percent)),
        },
        BTN_CANCEL,
      ],
    },
  );
};

/**
 * @deprecated
 * it seems not used anywhere.
 * so we not translated for now
 */
const replenishToParModal = (
  showItemOptions: boolean = true,
  title: string = null,
): IModalDialogOptions => {
  return merge(
    {},
    {
      title: isEmpty(title) ? 'Replenish to PAR' : title,
      validation: true,
      modalClass: `modal-md hidden-header-checkbox`,
      buttons: [BTN_CONTINUE, BTN_CANCEL],
      schema: {
        type: 'object',
        properties: {
          itemOption: { type: 'string', required: showItemOptions },
          percent: {
            type: 'number',
            default: TRANSFER_REPLENISH_DEFAULT_PERCENT,
          },
        },
      },
      value: {
        itemOption: REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
      },
      formRules: {
        fields: {
          percent: {
            title: 'Replenish to % of PAR',
            widget: WIDGET_COMPONENT.input_currency,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'standard-width',
            minimum: 1,
            maximum: 999,
            widgetData: {
              currencySymbolFn: (): string => PERCENTAGE_SIGN,
              positionFn: (): string => POSITION_RIGHT,
              precisionFn: (): number => 0,
              defaultValueByClearFn: (): number =>
                TRANSFER_REPLENISH_DEFAULT_PERCENT,
            },
          },
          itemOption: getReplenishItemOptions({
            showItemOptions,
            itemOptionsClass: 'select-full-width max-inline-col-width',
          }),
        },
      },
    },
  );
};

const viewSettingModal = (hideEmptyRow: boolean): IModalDialogOptions => {
  return {
    title: I18n.Common.ViewSetting,
    validation: true,
    modalClass: `modal-md`,
    buttons: [BTN_DONE, BTN_CANCEL],
    value: {
      hide_empty_rows: hideEmptyRow,
    },
    schema: {
      type: 'object',
      properties: {
        hide_empty_rows: {
          type: 'boolean',
        },
      },
    },
    formRules: {
      fields: {
        hide_empty_rows: {
          title: I18n.Common.HideEmptyRows,
          widget: WIDGET_COMPONENT.switch,
          widgetFamily: WIDGET_FAMILY.XUX,
          class: 'standard-width',
        },
      },
    },
  };
};

const duplicatePrepareModal = (params: {
  defaultValue?: {
    selectedEventIds: string[];
  };
  i18nService: XVII18nService;
}): IModalDialogOptions => {
  const { defaultValue } = params;

  const preTransferQueryFilter = getTransferEventQueryFilter(
    TRANSFER_TYPE.PRE_EVENT_TRANSFER,
  );
  const duringTransferQueryFilter = getTransferEventQueryFilter(
    TRANSFER_TYPE.DURING_EVENT_TRANSFER,
  );

  return {
    title: I18n.Forms.TransferForm.DuplicateTransfer,
    modalClass: `modal-md`,
    buttons: [BTN_CONTINUE, BTN_CANCEL],
    value: {
      events: defaultValue?.selectedEventIds,
    },
    schema: {
      type: 'object',
      properties: {
        events: {
          type: 'array',
        },
      },
    },
    formRules: {
      fields: {
        events: {
          title: I18n.EntityNames.Event,
          widgetFamily: WIDGET_FAMILY.XUX,
          widget: WIDGET_COMPONENT.select_event_with_grid_modal,
          widgetData: {
            filterForPortalSelector: [
              `((${preTransferQueryFilter}) or (${duringTransferQueryFilter}))`,
            ],
            sortForPortalSelector: [
              'status desc',
              'event_days/start_date_time asc',
              'name asc',
            ],
            updateSelectOptionsWhenSingleSelected: true,
            minItems: 0,
            maxItems: 1,
            useInputUrl: false,
            select: EVENT_SELECTOR_SELECT,
            itemSourceIdKeyPath: 'id',
          },
        },
      },
    },
  };
};

const cannotDuplicatedModal = (modalParams: {
  selectOptions: IBaseEntityType[];
  subTitle: Observable<string>;
  sitesDisplayCount$: Observable<number>;
  i18nService: XVII18nService;
}): IModalDialogOptions => {
  const { selectOptions, subTitle, sitesDisplayCount$, i18nService } =
    modalParams;

  return {
    title: I18n.InfoMessages.UnavailableItemList,
    subTitleObs: subTitle,
    buttons: [
      {
        ...BTN_OK,
        class: 'pull-right btn-primary',
      },
    ],
    modalClass: `modal-lg`,
    schema: {
      type: 'object',
      properties: {
        list: {
          type: 'array',
        },
      },
    },
    formRules: {
      fields: {
        list: {
          title: null,
          ignoreAutoTitleWhenTranslate: true,
          widgetFamily: WIDGET_FAMILY.XUX,
          widget: WIDGET_COMPONENT.grid_list,
          widgetData: {
            selectOptions,
            multiselect: false,
            displayCountObs: sitesDisplayCount$,
            gridHeight: '300px',
            valueField: 'item_entity_id',
            columns: [
              {
                title: I18n.Forms.GridForm.ItemName,
                path: 'item_name',
                defaultView: true,
                sortable: true,
                sort: SORT_ORDERS.ASC,
                sortingOrder: [SORT_ORDERS.ASC, SORT_ORDERS.DESC],
                comparator: stringCaseInsensitiveComparator,
                filterParams: {
                  quickFilter: true,
                },
              },
              {
                title: I18n.Forms.GridForm.Reason,
                path: 'reasons',
                valueFormatter: (params) =>
                  join(
                    map(params?.value as Array<string>, (reason) =>
                      i18nService.translate(
                        DUPLICATE_TRANSFER_ERROR_OPTIONS?.[reason]?.text ?? '',
                      ),
                    ),
                    ',',
                  ),
                defaultView: true,
                filterType: CELL_FILTER.entity,
                filterParams: {
                  default: {
                    type: 'containsAny' as FilterOptionType,
                  },
                  filterOptions: ['containsAny'] as FilterOptionType[],
                  values: chain(DUPLICATE_TRANSFER_ERROR_OPTIONS)
                    .reduce((total, curValue) => concat(total, [curValue]), [])
                    .sort()
                    .value(),
                },
              },
            ],
          },
        },
      },
    },
  };
};

export const transferModalDefinition = {
  replenishToParModal,
  bulkReplenishToParModal,
  viewSettingModal,
  duplicatePrepareModal,
  cannotDuplicatedModal,
};
